import { useEffect, useRef, useState } from 'react';
import { Film } from 'lucide-react';

import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import { cn } from '@/lib/cn';
import type { AnteprimaEmote as Anteprima } from '@/lib/avatarEmoteAnteprima';

/**
 * L'anteprima di una clip nel selettore di «Le mie emote» (I-618 punto 4): MAI un riquadro nero muto.
 * In ordine: il video (`DualStreamVideo`, colore + alfa, in loop, muto) se `anima` e i flussi sono pronti; sotto di lui il poster
 * (`preview.posterUrl`, altrimenti il `posterUrl` della clip); senza nessun poster, o se non si carica, un segnaposto chiaro.
 * `anima` lo decide il genitore (movimento ridotto, tessera in vista, tetto dei lettori): qui, se e' falso, i due lettori NON esistono.
 * `data-anteprima` = `video` | `poster` | `segnaposto` (cio' che si vede adesso).
 */
export function AnteprimaEmote({
  clipId,
  preview,
  posterUrl,
  anima,
  onVisibile,
  className,
}: {
  clipId?: string;
  preview?: Anteprima;
  /** Il poster della clip, di ripiego se la preview non ne ha. */
  posterUrl?: string;
  anima?: boolean;
  /** Chiamata quando la tessera entra o esce dalla vista (IntersectionObserver). Senza di lui non si osserva. */
  onVisibile?: (clipId: string, visibile: boolean) => void;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [pronto, setPronto] = useState(false);
  const [videoRotto, setVideoRotto] = useState(false);
  const [posterRotto, setPosterRotto] = useState(false);
  const poster = preview?.posterUrl ?? posterUrl;
  const video = !!anima && !!preview && !videoRotto;

  // Fuori dal tetto o fuori vista i lettori si smontano (e si liberano): la tessera torna al poster.
  useEffect(() => {
    if (!video) setPronto(false);
  }, [video]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !clipId || !onVisibile) return;
    // Senza IntersectionObserver (jsdom, WebView vecchie) tutte le tessere valgono «in vista»: il tetto le limita comunque.
    if (typeof IntersectionObserver === 'undefined') {
      onVisibile(clipId, true);
      return () => onVisibile(clipId, false);
    }
    const o = new IntersectionObserver((voci) => {
      const ultima = voci[voci.length - 1];
      if (ultima) onVisibile(clipId, ultima.isIntersecting);
    });
    o.observe(el);
    return () => {
      o.disconnect();
      onVisibile(clipId, false);
    };
  }, [clipId, onVisibile]);

  const mostraPoster = !!poster && !posterRotto;
  const cosa = video && pronto ? 'video' : mostraPoster ? 'poster' : 'segnaposto';
  return (
    <span ref={ref} data-anteprima={cosa} className={cn('relative block overflow-hidden bg-bg', className)}>
      {mostraPoster ? (
        <img
          src={poster}
          alt=""
          loading="lazy"
          onError={() => setPosterRotto(true)}
          className={cn('h-full w-full object-cover', cosa === 'video' && 'opacity-0')}
        />
      ) : (
        <span className="grid h-full w-full place-items-center text-fg-muted">
          <Film aria-hidden className="h-1/3 w-1/3" />
        </span>
      )}
      {video && (
        <span className={cn('absolute inset-0', !pronto && 'opacity-0')}>
          <DualStreamVideo
            rgbUrl={preview.rgbUrl}
            alphaUrl={preview.alphaUrl}
            onPronto={() => setPronto(true)}
            onErrore={() => setVideoRotto(true)}
            liberaAllUscita
          />
        </span>
      )}
    </span>
  );
}
